import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';
import { usePerms } from '@/lib/perms';

/** Настройки → «Расходы и согласования» (этап 1.3, ТЗ п.3.6–3.7): пороги согласования, тарифы прямых расходов
 * (самовывоз за рейс + вместимость машины, доставка за CBM и минимум, установка за единицу по категории) и правила наценок
 * по категории / партнёру. Редактирует admin; остальные видят. */
type Rate = { id?: string; kind: 'pickup_trip' | 'delivery_cbm' | 'delivery_min' | 'installation_unit' | 'other'; category: string | null; value: number; vehicleCbm: number | null; active: boolean };
type Rule = { id?: string; scope: 'category' | 'partner' | 'partner_category'; partnerId: string | null; category: string | null; markupPct: number; active: boolean };
type Thresholds = { min_product_margin_pct: number; min_contribution_pct: number };
const CATEGORIES = ['workstations', 'desks', 'chairs', 'storage', 'components', 'meetingTables', 'softSeating', 'pedestals', 'execDesks', 'partitions', 'reception', 'coffeeTables', 'services'];
const inputCls = 'tnum h-8 w-full rounded-md border border-background-200 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400 disabled:bg-background-100';

export default function CostsTab({ onToast }: { onToast: (msg: string, detail?: string, tone?: 'success' | 'error') => void }) {
  const { t } = useTranslation();
  const { admin } = usePerms();
  const [rates, setRates] = useState<Rate[] | null>(null);
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [partners, setPartners] = useState<{ id: string; shortName: string }[]>([]);
  const [th, setTh] = useState<Thresholds | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, ru, s, meta] = await Promise.all([api.get<Rate[]>('/settings/cost-rates'), api.get<Rule[]>('/settings/pricing-rules'), api.get<Thresholds>('/settings').catch(() => null), api.get<{ partners: { id: string; shortName: string }[] }>('/catalog/meta').catch(() => ({ partners: [] }))]);
      setRates(r.filter((x) => x.active));
      setRules(ru);
      setPartners(meta.partners);
      if (s) setTh({ min_product_margin_pct: Number(s.min_product_margin_pct), min_contribution_pct: Number(s.min_contribution_pct) });
    } catch (e) {
      onToast(t('ui.error'), (e as ApiError).code, 'error');
    }
  }, [onToast, t]);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!rates || !rules) return;
    setBusy(true);
    try {
      await api.put('/settings/cost-rates', { rates });
      await api.put('/settings/pricing-rules', { rules });
      if (th) await api.put('/settings', { values: th });
      await load();
      onToast(t('costs.saved'));
    } catch (e) {
      onToast(t('ui.error'), (e as ApiError).code, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!rates || !rules) return <p className="mt-5 text-[12px] text-foreground-500">{t('ui.loading')}</p>;
  const one = (kind: Rate['kind']) => rates.find((r) => r.kind === kind);
  const setOne = (kind: Rate['kind'], patch: Partial<Rate>) =>
    setRates((rs) => {
      const list = rs ?? [];
      return list.some((r) => r.kind === kind) ? list.map((r) => (r.kind === kind ? { ...r, ...patch } : r)) : [...list, { kind, category: null, value: 0, vehicleCbm: null, active: true, ...patch }];
    });
  const installs = rates.filter((r) => r.kind === 'installation_unit');
  const catLabel = (c: string) => t(`category.${c}`, { defaultValue: c });

  return (
    <div className="mt-5 flex flex-col gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground-950">{t('costs.title')}</h2>
          <p className="mt-0.5 max-w-[720px] text-[11.5px] leading-4 text-foreground-500">{t('costs.sub')}</p>
        </div>
        {admin && (
          <button type="button" disabled={busy} onClick={() => void save()} className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-50">
            <i className="ri-save-line text-[14px]" /> {busy ? t('ui.saving') : t('set.save')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h3 className="text-[13px] font-semibold text-foreground-950">{t('costs.thresholdsTitle')}</h3>
          <p className="mt-0.5 text-[11px] leading-4 text-foreground-500">{t('costs.thresholdsSub')}</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('costs.minMargin')}</span>
              <div className="flex items-center gap-2">
                <input type="number" min={0} max={100} disabled={!admin} value={th?.min_product_margin_pct ?? ''} onChange={(e) => setTh((x) => ({ ...(x ?? { min_product_margin_pct: 12, min_contribution_pct: 8 }), min_product_margin_pct: Number(e.target.value) }))} className={inputCls} />
                <span className="text-[12px] text-foreground-500">%</span>
              </div>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('costs.minContribution')}</span>
              <div className="flex items-center gap-2">
                <input type="number" min={-100} max={100} disabled={!admin} value={th?.min_contribution_pct ?? ''} onChange={(e) => setTh((x) => ({ ...(x ?? { min_product_margin_pct: 12, min_contribution_pct: 8 }), min_contribution_pct: Number(e.target.value) }))} className={inputCls} />
                <span className="text-[12px] text-foreground-500">%</span>
              </div>
            </label>
          </div>
          <p className="mt-3 text-[10.5px] leading-4 text-foreground-400">{t('costs.whoApproves')}</p>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <h3 className="text-[13px] font-semibold text-foreground-950">{t('costs.ratesTitle')}</h3>
          <p className="mt-0.5 text-[11px] leading-4 text-foreground-500">{t('costs.ratesSub')}</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('costs.pickupTrip')}</span>
              <input type="number" min={0} disabled={!admin} value={one('pickup_trip')?.value ?? ''} onChange={(e) => setOne('pickup_trip', { value: Number(e.target.value) })} className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('costs.vehicleCbm')}</span>
              <input type="number" min={1} step={0.5} disabled={!admin} value={one('pickup_trip')?.vehicleCbm ?? ''} onChange={(e) => setOne('pickup_trip', { vehicleCbm: Number(e.target.value) || null })} className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('costs.deliveryCbm')}</span>
              <input type="number" min={0} disabled={!admin} value={one('delivery_cbm')?.value ?? ''} onChange={(e) => setOne('delivery_cbm', { value: Number(e.target.value) })} className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('costs.deliveryMin')}</span>
              <input type="number" min={0} disabled={!admin} value={one('delivery_min')?.value ?? ''} onChange={(e) => setOne('delivery_min', { value: Number(e.target.value) })} className={inputCls} />
            </label>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-[13px] font-semibold text-foreground-950">{t('costs.installTitle')}</h3>
              <p className="mt-0.5 text-[11px] leading-4 text-foreground-500">{t('costs.installSub')}</p>
            </div>
            {admin && (
              <button type="button" onClick={() => setRates((rs) => [...(rs ?? []), { kind: 'installation_unit', category: CATEGORIES.find((c) => !installs.some((i) => i.category === c)) ?? 'chairs', value: 0, vehicleCbm: null, active: true }])} className="h-7 cursor-pointer whitespace-nowrap rounded border border-background-300 px-2.5 text-[11px] font-medium text-foreground-700 hover:bg-background-100">
                <i className="ri-add-line" /> {t('costs.addRow')}
              </button>
            )}
          </div>
          <table className="mt-3 w-full text-[12px]">
            <tbody>
              {installs.map((r, i) => (
                <tr key={r.id ?? `n${i}`} className="border-b border-background-200/70 last:border-0">
                  <td className="py-1.5 pr-2">
                    <select disabled={!admin} value={r.category ?? ''} onChange={(e) => setRates((rs) => (rs ?? []).map((x) => (x === r ? { ...x, category: e.target.value } : x)))} className="h-8 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400 disabled:bg-background-100">
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {catLabel(c)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="w-32 py-1.5 pr-2">
                    <input type="number" min={0} disabled={!admin} value={r.value} onChange={(e) => setRates((rs) => (rs ?? []).map((x) => (x === r ? { ...x, value: Number(e.target.value) } : x)))} className={inputCls} aria-label={t('costs.installTitle')} />
                  </td>
                  <td className="w-8 py-1.5 text-right">
                    {admin && (
                      <button type="button" aria-label={t('onb.fRemove')} onClick={() => setRates((rs) => (rs ?? []).filter((x) => x !== r))} className="cursor-pointer text-foreground-300 hover:text-rose-600">
                        <i className="ri-delete-bin-line text-[13px]" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {installs.length === 0 && (
                <tr>
                  <td className="py-2 text-[11.5px] text-foreground-500">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-[13px] font-semibold text-foreground-950">{t('costs.rulesTitle')}</h3>
              <p className="mt-0.5 text-[11px] leading-4 text-foreground-500">{t('costs.rulesSub')}</p>
            </div>
            {admin && (
              <button type="button" onClick={() => setRules((rs) => [...(rs ?? []), { scope: 'category', partnerId: null, category: 'chairs', markupPct: 40, active: true }])} className="h-7 cursor-pointer whitespace-nowrap rounded border border-background-300 px-2.5 text-[11px] font-medium text-foreground-700 hover:bg-background-100">
                <i className="ri-add-line" /> {t('costs.addRow')}
              </button>
            )}
          </div>
          <table className="mt-3 w-full text-[12px]">
            <tbody>
              {rules.map((r, i) => (
                <tr key={r.id ?? `n${i}`} className="border-b border-background-200/70 last:border-0">
                  <td className="py-1.5 pr-2">
                    <select disabled={!admin} value={r.scope} onChange={(e) => setRules((rs) => (rs ?? []).map((x) => (x === r ? { ...x, scope: e.target.value as Rule['scope'] } : x)))} className="h-8 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400 disabled:bg-background-100">
                      {(['category', 'partner', 'partner_category'] as const).map((s) => (
                        <option key={s} value={s}>
                          {t(`costs.scope.${s}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2">
                    {r.scope !== 'category' && (
                      <select disabled={!admin} value={r.partnerId ?? ''} onChange={(e) => setRules((rs) => (rs ?? []).map((x) => (x === r ? { ...x, partnerId: e.target.value || null } : x)))} className="h-8 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400 disabled:bg-background-100">
                        <option value="">—</option>
                        {partners.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.shortName}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    {r.scope !== 'partner' && (
                      <select disabled={!admin} value={r.category ?? ''} onChange={(e) => setRules((rs) => (rs ?? []).map((x) => (x === r ? { ...x, category: e.target.value || null } : x)))} className="h-8 w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400 disabled:bg-background-100">
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {catLabel(c)}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="w-24 py-1.5 pr-2">
                    <div className="flex items-center gap-1">
                      <input type="number" min={0} disabled={!admin} value={r.markupPct} onChange={(e) => setRules((rs) => (rs ?? []).map((x) => (x === r ? { ...x, markupPct: Number(e.target.value) } : x)))} className={inputCls} aria-label={t('set.markup')} />
                      <span className="text-[11px] text-foreground-500">%</span>
                    </div>
                  </td>
                  <td className="w-8 py-1.5 text-right">
                    {admin && (
                      <button type="button" aria-label={t('onb.fRemove')} onClick={() => setRules((rs) => (rs ?? []).filter((x) => x !== r))} className="cursor-pointer text-foreground-300 hover:text-rose-600">
                        <i className="ri-delete-bin-line text-[13px]" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr>
                  <td className="py-2 text-[11.5px] text-foreground-500">{t('costs.noRules')}</td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-2 text-[10.5px] leading-4 text-foreground-400">{t('costs.rulesOrder')}</p>
        </div>
      </div>
    </div>
  );
}
